import { isSVGElement } from '@adflow/types';
import type { AdElement } from '@adflow/types';

import type { Point } from './viewport';

/**
 * The compiled SVG layer can be selected alone but never as part of a multi-selection.
 * Single source of truth for the rule: used by the store and the canvas gestures. It keys off
 * the element type (not hit-target membership) so hidden or otherwise non-hittable layers
 * stay in a group.
 */
export function withoutCompiledSvgInMultiSelection(
  elements: readonly AdElement[],
  ids: string[],
): string[] {
  if (ids.length < 2) return ids;
  const svgIds = new Set(elements.filter(isSVGElement).map((el) => el.id));
  return ids.filter((id) => !svgIds.has(id));
}

export type AxisAlignedBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type HitTarget = {
  id: string;
  z: number;
  bounds: AxisAlignedBounds;
  locked?: boolean | null;
  hidden?: boolean | null;
};

/** The compiled SVG frame is never a canvas hit target; it is only selectable from the Elements list. */
export function hitTargetsFromElements(elements: readonly AdElement[]): HitTarget[] {
  return elements
    .filter((element) => !isSVGElement(element))
    .map((element) => ({
      id: element.id,
      z: element.position.z,
      bounds: {
        x: element.position.x,
        y: element.position.y,
        width: element.size.width,
        height: element.size.height,
      },
      locked: element.locked === true,
      hidden: element.hidden === true,
    }));
}

export function isSameSelection(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export type PressSelection = {
  selection: string[];
  /** False for a locked element or a press that deselected it. */
  startsDrag: boolean;
  /**
   * Pressing a member of a multi-selection keeps the group so it can be dragged together;
   * releasing without movement collapses the selection to this id.
   */
  collapseToId: string | null;
};

/** Selection after pressing `picked` on the canvas, with Shift/Cmd/Ctrl as `additive`. */
export function selectionOnPress(args: {
  elements: readonly AdElement[];
  targets: readonly HitTarget[];
  selected: string[];
  picked: string;
  additive: boolean;
}): PressSelection {
  const { elements, targets, selected, picked, additive } = args;
  const locked = targets.find((target) => target.id === picked)?.locked === true;
  const wasSelected = selected.includes(picked);

  let selection: string[];
  let collapseToId: string | null = null;
  if (additive) {
    selection = wasSelected ? selected.filter((id) => id !== picked) : [...selected, picked];
  } else if (wasSelected && selected.length > 1 && !locked) {
    selection = selected;
    collapseToId = picked;
  } else {
    selection = [picked];
  }

  selection = withoutCompiledSvgInMultiSelection(elements, selection);
  const startsDrag = !locked && selection.includes(picked);
  return { selection, startsDrag, collapseToId: startsDrag ? collapseToId : null };
}

/** When z ties, higher array index wins (stable, documented tie-break). */
export function pickTopmostAtPoint(
  targets: readonly HitTarget[],
  point: Point,
  options?: { includeLocked?: boolean; includeHidden?: boolean },
): string | null {
  const includeLocked = options?.includeLocked ?? false;
  const includeHidden = options?.includeHidden ?? false;

  const hits = targets
    .map((target, index) => ({ target, index }))
    .filter(({ target }) => {
      if (!includeLocked && target.locked === true) return false;
      if (!includeHidden && target.hidden === true) return false;
      return pointInBounds(point, target.bounds);
    });

  if (hits.length === 0) {
    return null;
  }

  hits.sort((a, b) => {
    if (b.target.z !== a.target.z) {
      return b.target.z - a.target.z;
    }
    return b.index - a.index;
  });

  return hits[0]?.target.id ?? null;
}

/** Axis-aligned rectangle spanned by two corner points, in any drag direction. */
export function normalizeRect(a: Point, b: Point): AxisAlignedBounds {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/** Edges that touch count as intersecting. */
export function boundsIntersect(a: AxisAlignedBounds, b: AxisAlignedBounds): boolean {
  return (
    a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y
  );
}

/**
 * Ids of every target the marquee rectangle intersects, in target order.
 * Locked and hidden targets are skipped unless explicitly included.
 */
export function pickTargetsInRect(
  targets: readonly HitTarget[],
  rect: AxisAlignedBounds,
  options?: { includeLocked?: boolean; includeHidden?: boolean },
): string[] {
  const includeLocked = options?.includeLocked ?? false;
  const includeHidden = options?.includeHidden ?? false;

  return targets
    .filter((target) => {
      if (!includeLocked && target.locked === true) return false;
      if (!includeHidden && target.hidden === true) return false;
      return boundsIntersect(rect, target.bounds);
    })
    .map((target) => target.id);
}

export function pointInBounds(point: Point, bounds: AxisAlignedBounds): boolean {
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
}

export function combinedAxisAlignedBounds(
  targets: readonly HitTarget[],
  ids: readonly string[],
): AxisAlignedBounds | null {
  const selected = targets.filter((target) => ids.includes(target.id));
  if (selected.length === 0) {
    return null;
  }

  const xs = selected.map((t) => t.bounds.x);
  const ys = selected.map((t) => t.bounds.y);
  const rights = selected.map((t) => t.bounds.x + t.bounds.width);
  const bottoms = selected.map((t) => t.bounds.y + t.bounds.height);

  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const right = Math.max(...rights);
  const bottom = Math.max(...bottoms);

  return { x, y, width: right - x, height: bottom - y };
}
